import { CoralogixRum, CoralogixLogSeverity } from '@coralogix/browser';
import type { CoralogixBrowserSdkConfig } from '@coralogix/browser';
import type { BootData } from '@/types/boot';
import { getEnvVariable } from '@/utils/get-env-variable';

const CORALOGIX_RUM_SAMPLING_RATE = 100;
const MAX_PENDING_RUM_LOGS = 64;

type PendingRumLog = {
  level: CoralogixLogSeverity;
  message: string;
  data?: Record<string, unknown> | null;
  labels?: Record<string, string | number | boolean>;
};

// Init state is read from `CoralogixRum.isInited` rather than tracked locally:
// `CoralogixRum.init` swallows its own failures (it try/catches internally and
// only `console.warn`s, and bails silently on a non-browser env or a rejected
// config), so it returning does NOT mean RUM is live. A local "initialized"
// flag would claim it is and replay the queue into a `log()` that drops every
// entry.
let pendingRumLogs: PendingRumLog[] = [];

// ACDEV-25945: order_id is passed as a GLOBAL RUM label so every event the SDK
// ships (JS errors, network requests, resources, and all custom logs — including
// the Apple Pay failure log) is queryable by order without touching each call
// site. Init runs after the order id is known (see CheckoutContent), so it is
// present from the first shipped event.
export const initCoralogixRum = (customerEmail?: string, bootData?: BootData, orderId?: string): void => {
  const publicKey = getEnvVariable('VITE_CORALOGIX_RUM_PUBLIC_KEY');

  if (!publicKey) {
    console.warn('Coralogix RUM public key not found. RUM monitoring will be disabled.');
    return;
  }

  if (CoralogixRum.isInited) {
    console.warn('Coralogix RUM is already initialized.');
    return;
  }

  const publisherId = bootData?.checkoutTheme?.publisherId || bootData?.theme?.publisherId || 'unknown';

  const config: CoralogixBrowserSdkConfig = {
    public_key: publicKey,
    stringifyCustomLogData: true,
    application: 'checkout-v3',
    version: '1.0.0',
    coralogixDomain: getEnvVariable('VITE_CORALOGIX_DOMAIN', 'US2') as CoralogixBrowserSdkConfig['coralogixDomain'],
    sessionConfig: {
      // Ship ALL sampled sessions, not just ones that hit an error. The
      // conversion-diagnostics funnel (present → pay) targets the *silent*
      // Android card-path drop-off, whose sessions have near-zero JS errors —
      // with `onlyWithErrorConfig.enable: true` those exact sessions (and their
      // diagnostics logs) would be discarded before shipping. Trade-off:
      // higher RUM ingestion volume at the 100% sample rate below.
      onlyWithErrorConfig: {
        enable: false,
      },
      sessionSampleRate: CORALOGIX_RUM_SAMPLING_RATE,
      alwaysTrackSessionsWithErrors: true,
    },
    user_context: {
      user_id: '',
      user_email: customerEmail || 'anonymous@example.com',
      user_name: '',
    },
    labels: {
      publisherId: publisherId,
      publisherName: bootData?.theme?.publisherName || 'unknown',
      environment: getEnvVariable('VITE_ENV', 'production'),
      platform: 'web',
      order_id: orderId,
    },
  };

  try {
    CoralogixRum.init(config);
    if (CoralogixRum.isInited) {
      replayPendingLogs();
    } else {
      console.warn('Coralogix RUM init did not take effect. Queued logs will not be shipped.');
    }
  } catch (error) {
    console.error('Failed to initialize Coralogix RUM:', error);
  }
};

export const setRumUserContext = (userContext: { user_email: string; user_name: string; user_id: string }): void => {
  if (!CoralogixRum.isInited) {
    return;
  }

  try {
    CoralogixRum.setUserContext(userContext);
  } catch (error) {
    console.error('Failed to set RUM user context:', error);
  }
};

export const updateRumCustomerData = (sessionToken: string, customerEmail?: string): void => {
  if (!CoralogixRum.isInited) {
    return;
  }

  setRumUserContext({
    user_id: sessionToken,
    user_email: customerEmail || 'anonymous@example.com',
    user_name: '',
  });
};

export const logToCoralogix = (
  level: CoralogixLogSeverity,
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  // Queue rather than drop: init is deferred to idle (see CheckoutContent), so
  // `Checkout presented` is emitted before RUM is live. The cap bounds
  // retention for the case where init never lands at all — no separate
  // "RUM disabled" flag is needed, since a missing public key is a
  // build-time constant that cannot flip mid-session.
  if (!CoralogixRum.isInited) {
    enqueuePendingLog({ level, message, data, labels });
    return;
  }

  try {
    CoralogixRum.log(level, message, data || undefined, labels);
  } catch (error) {
    console.error('Failed to send log to Coralogix:', error);
  }
};

export const logDebug = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Debug, message, data, labels);
};

export const logInfo = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Info, message, data, labels);
};

export const logWarn = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Warn, message, data, labels);
};

export const logError = (
  message: string,
  errorData?: Error | unknown,
  additionalData?: Record<string, unknown>,
  labels?: Record<string, string | number | boolean>
): void => {
  const error = errorData instanceof Error ? errorData : null;
  const data = {
    ...additionalData,
    error: errorData
      ? {
          name: error?.name || 'Error',
          message: error?.message || String(errorData),
          stack: error?.stack,
        }
      : undefined,
  };

  logToCoralogix(CoralogixLogSeverity.Error, message, data, labels);
};

export const logCritical = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Critical, message, data, labels);
};

export const reportError = (message: string, data?: Record<string, unknown> | null): void => {
  if (!CoralogixRum.isInited) {
    return;
  }

  try {
    CoralogixRum.error(message, data || undefined);
  } catch (err) {
    console.error('Failed to report error to Coralogix:', err);
  }
};

/**
 * There is deliberately no `flushLogs` here.
 *
 * The SDK exports through an OTel `BatchSpanProcessor` with
 * `scheduledDelayMillis = 2000` and ships no unload-safe transport — its
 * bundles contain no `sendBeacon`, no `keepalive` fetch and no `pagehide`
 * handling — so anything logged in the last <= 2s of a page's life goes down
 * with the page. That window is exactly where the mobile 3DS failures live
 * (ACDEV-26410: dead sessions go dark 0.3-2.5s after the moment of interest),
 * which is why those sessions read as silent rather than failed.
 *
 * `shutdown()` looks like the escape hatch and is not one. It never touches the
 * batch processor or the tracer provider: it tears down the instrumentations
 * and calls `CoralogixExporter.shutdown()`, which only sets `isActive = false`,
 * and `export()` then early-returns and drops its spans. Calling it on
 * `pagehide` would therefore discard the queued batch it appears to rescue.
 *
 * Nothing in-page can flush, so instruments that must survive a kill are built
 * to emit incrementally instead — see the heartbeat in `paymentDiagnostics`,
 * whose last delivered beat is what dates the end of execution. A real fix
 * needs an unload-safe sink of our own (`sendBeacon` / `keepalive` fetch to an
 * endpoint we own), which is backend work and out of scope here.
 */
export const setCoralogixLabels = (labels: Record<string, string | number | boolean>): void => {
  if (!CoralogixRum.isInited) {
    return;
  }

  try {
    // setLabels replaces the session map. Spread current labels or
    // environment / publisher / platform from init are wiped — which is
    // what made Safari pay events drop out of `labels.environment == production`.
    CoralogixRum.setLabels({ ...CoralogixRum.getLabels(), ...labels });
  } catch (error) {
    console.error('Failed to set Coralogix labels:', error);
  }
};

function enqueuePendingLog(entry: PendingRumLog): void {
  if (pendingRumLogs.length >= MAX_PENDING_RUM_LOGS) {
    pendingRumLogs.shift();
  }
  pendingRumLogs.push(entry);
}

function replayPendingLogs(): void {
  const queued = pendingRumLogs;
  pendingRumLogs = [];
  for (const entry of queued) {
    try {
      CoralogixRum.log(entry.level, entry.message, entry.data || undefined, entry.labels);
    } catch (error) {
      console.error('Failed to send log to Coralogix:', error);
    }
  }
}

export default {
  initCoralogixRum,
  setRumUserContext,
  updateRumCustomerData,
  logToCoralogix,
  logInfo,
  logWarn,
  logError,
  logDebug,
  logCritical,
  reportError,
  setCoralogixLabels,
};
